import { Autocomplete, FormControl, FormHelperText, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { COUNTRIES } from 'utils/countries.utils';
import CountryRiskLevel, {
  TCountryRiskLevel,
} from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import isEqual from 'lodash/isEqual';
import React, { ChangeEvent, FC, memo, useMemo } from 'react';
import FlexBox from 'Components/FlexBox';

interface ICountryAutocompleteProps {
  value?: string | null; // Country code
  placeholder?: string;
  size?: 'small' | 'medium';
  hasError?: boolean;
  showCountryRisk?: boolean;
  errorMessage?: string;
  euCountriesOnly?: boolean; // If true, only EU countries will be shown
  onCountrySelect: (countryCode: string | null) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

const StyledAutocomplete = styled(
  memo(
    Autocomplete<{ code: string; label: string; riskLevel?: TCountryRiskLevel }>,
    (prev, next) => {
      return isEqual(prev, next) && isEqual(prev.value, next.value);
    }
  )
)(() => ({
  '& .MuiAutocomplete-inputRoot': {
    paddingLeft: '16px !important',
    fontSize: 12,
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
    padding: 4,
    '& .MuiInputBase-input': {
      fontSize: 12,
    },
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]':
    {
      padding: 4,
      '& .MuiInputBase-input': {
        fontSize: 12,
      },
    },
}));

const StyledFormHelperText = styled(FormHelperText)(() => ({
  '&.MuiFormHelperText-root': {
    marginTop: -2,
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    flexWrap: 'nowrap',
  },
}));

const CountryAutocomplete: FC<ICountryAutocompleteProps> = ({
  value = null,
  placeholder = 'Select country',
  hasError,
  errorMessage = 'Please select a country',
  size = 'medium',
  euCountriesOnly,
  showCountryRisk,
  disabled,
  onCountrySelect,
  'data-testid': testId,
}) => {
  const selectedCountry = useMemo(
    () => COUNTRIES.find(country => country.code === value?.toLocaleUpperCase()),
    [value]
  );

  const handleSelectCountry = (event: ChangeEvent<unknown>, country: { code: string } | null) => {
    onCountrySelect(country?.code || null);
  };

  return (
    <FormControl fullWidth size={size} error={hasError}>
      <StyledAutocomplete
        value={selectedCountry || null}
        options={euCountriesOnly ? COUNTRIES.filter(country => country.isInEU) : COUNTRIES}
        disabled={disabled}
        data-testid={testId}
        fullWidth
        size={size}
        renderOption={(props, option) => (
          <li {...props} key={option.code} data-testid={`${testId}-option`}>
            {option?.label}
            {showCountryRisk && option.riskLevel && (
              <FlexBox ml={1}>
                <CountryRiskLevel riskLevel={option.riskLevel} />
              </FlexBox>
            )}
          </li>
        )}
        renderInput={params => (
          <StyledTextField
            {...params}
            error={hasError}
            placeholder={placeholder}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment:
                showCountryRisk &&
                selectedCountry?.riskLevel &&
                params.inputProps.value === selectedCountry.label ? (
                  <FlexBox maxWidth="50%">
                    <CountryRiskLevel riskLevel={selectedCountry.riskLevel} />
                    {params.InputProps.endAdornment}
                  </FlexBox>
                ) : (
                  params.InputProps.endAdornment
                ),
            }}
            onKeyPress={keyEvent => {
              // Prevents the form from being submitted on enter.
              if (keyEvent.key === 'Enter') {
                keyEvent.preventDefault();
              }
            }}
          />
        )}
        onChange={handleSelectCountry}
      />
      {hasError && <StyledFormHelperText>{errorMessage}</StyledFormHelperText>}
    </FormControl>
  );
};

export default CountryAutocomplete;
